/**
 * Harbour off-ramps: snooze (looked, not now), park (reason required),
 * Ask sales / Ask product (note required).
 */

import {
  HARBOUR_COLUMN,
  HARBOUR_HANDOFF_SEND_LABEL,
  HARBOUR_HANDOFF_SEND_PRODUCT_LABEL,
  HARBOUR_HANDOFF_SEND_SALES_LABEL,
  HARBOUR_HANDOFF_SENDING_LABEL,
  HARBOUR_OUTCOME,
  HARBOUR_PARK_ACTION,
  HARBOUR_PARK_CLOSED_HINT,
  HARBOUR_PARK_COPY,
  HARBOUR_PARK_REASON,
  HARBOUR_PARK_RECOVERED_ACTION,
  HARBOUR_PARK_RECOVERED_TOAST,
  HARBOUR_PARK_TOAST,
  handoffRequiresNote,
  isHarbourParkedColumn,
  parkRequiresReason,
} from 'src/constants/harbour'
import { getHarbourSisterGroupVenueIds } from 'src/utils/harbourMappers'

export function canSnoozeHarbour({ alreadyTaken, column } = {}) {
  return !alreadyTaken && !isHarbourParkedColumn(column)
}

export function canParkHarbour({ alreadyTaken, column } = {}) {
  return !alreadyTaken && !isHarbourParkedColumn(column)
}

export function canHandoffHarbour({ alreadyOwned, alreadyTaken, column } = {}) {
  return (
    Boolean(alreadyOwned) && !alreadyTaken && !isHarbourParkedColumn(column)
  )
}

export function canSaveHarbourPark({ parkReason } = {}) {
  return parkRequiresReason(parkReason)
}

export function shouldShowHarbourParkRecoveredHint({
  outcome,
  lastOutcome,
} = {}) {
  return (
    outcome === HARBOUR_OUTCOME.CLOSED || lastOutcome === HARBOUR_OUTCOME.CLOSED
  )
}

export function getHarbourInitialParkReason({ lastOutcome } = {}) {
  return lastOutcome === HARBOUR_OUTCOME.CLOSED
    ? HARBOUR_PARK_REASON.RECOVERED
    : ''
}

export function getHarbourSisterGroupSiteCount(sisterGroup) {
  const fromSites = Array.isArray(sisterGroup?.sites)
    ? sisterGroup.sites.filter(site => site?.venueId).length
    : 0
  if (fromSites > 1) {
    return fromSites
  }
  const counted = Number(sisterGroup?.siteCount) || 0
  return counted > 1 ? counted : 1
}

export function getHarbourSnoozeCaption({ siteCount = 1 } = {}) {
  const count = Number(siteCount) || 1
  if (count > 1) {
    return `Looked, not now. Snoozes all ${count} sites. Stays on the radar.`
  }
  return 'Looked, not now. Stays on the radar.'
}

export function getHarbourSnoozeSuccessMessage({ days, siteCount = 1 } = {}) {
  const count = Number(siteCount) || 1
  if (count > 1) {
    return `Snoozed ${days} days · ${count} sites`
  }
  return `Snoozed ${days} days`
}

export function getHarbourParkCaption({
  recoveredHint = false,
  siteCount = 1,
} = {}) {
  if (recoveredHint) {
    return HARBOUR_PARK_CLOSED_HINT
  }
  const count = Number(siteCount) || 1
  if (count > 1) {
    return `${HARBOUR_PARK_COPY} Parks all ${count} sites.`
  }
  return HARBOUR_PARK_COPY
}

export function getHarbourParkSaveLabel(parkReason, { busy = false } = {}) {
  if (busy) {
    return 'Parking…'
  }
  if (parkReason === HARBOUR_PARK_REASON.RECOVERED) {
    return HARBOUR_PARK_RECOVERED_ACTION
  }
  return HARBOUR_PARK_ACTION
}

export function getHarbourParkSuccessMessage(
  parkReason,
  { siteCount = 1 } = {},
) {
  const count = Number(siteCount) || 1
  if (parkReason === HARBOUR_PARK_REASON.RECOVERED) {
    return count > 1
      ? `${HARBOUR_PARK_RECOVERED_TOAST} · ${count} sites`
      : HARBOUR_PARK_RECOVERED_TOAST
  }
  return count > 1
    ? `${HARBOUR_PARK_TOAST} · ${count} sites`
    : HARBOUR_PARK_TOAST
}

export function canSaveHarbourHandoff({ column, handoffNote } = {}) {
  return (
    handoffRequiresNote(column) && Boolean(String(handoffNote || '').trim())
  )
}

/**
 * Picker chips stay “Ask sales” / “Ask product”. The submit is Send.
 */
export function getHarbourHandoffSendLabel(column, { busy = false } = {}) {
  if (busy) {
    return HARBOUR_HANDOFF_SENDING_LABEL
  }
  if (column === HARBOUR_COLUMN.ASK_PRODUCT) {
    return HARBOUR_HANDOFF_SEND_PRODUCT_LABEL
  }
  if (column === HARBOUR_COLUMN.ASK_SALES) {
    return HARBOUR_HANDOFF_SEND_SALES_LABEL
  }
  return HARBOUR_HANDOFF_SEND_LABEL
}

export function buildHarbourSnoozeBody({ venueId, days, sisterGroup } = {}) {
  const body = { venueId, days }
  const venueIds = getHarbourSisterGroupVenueIds(sisterGroup, venueId)
  if (venueIds.length > 1) {
    body.venueIds = venueIds
  }
  return body
}

export function buildHarbourParkBody({
  venueId,
  parkReason,
  parkNote,
  sisterGroup,
} = {}) {
  const body = { venueId, parkReason }
  const note = String(parkNote || '').trim()
  if (note) {
    body.parkNote = note
  }
  const venueIds = getHarbourSisterGroupVenueIds(sisterGroup, venueId)
  if (venueIds.length > 1) {
    body.venueIds = venueIds
  }
  return body
}

export function buildHarbourUndoOfframpBody({ venueId, kind, venueIds } = {}) {
  const body = { venueId, kind }
  const ids = (Array.isArray(venueIds) ? venueIds : []).filter(Boolean)
  if (ids.length > 1) {
    body.venueIds = ids
  }
  return body
}

export function buildHarbourHandoffBody({ venueId, column, handoffNote } = {}) {
  return {
    venueId,
    column,
    handoffNote: String(handoffNote || '').trim(),
  }
}
